/**
 * --------------------------------------------------------------------------
 * IMPORTANT: This functionality is also present in save-as-pdf.js (to enable WYSIWYG editor support).
 * If you make changes here, please ensure that the same changes are reflected there.
 * --------------------------------------------------------------------------
 **/

'use client'

import { useEffect, useRef, useState } from 'react'
import { Button, OverlayArrow, Tooltip, TooltipTrigger } from 'react-aria-components'

const apiKey = process.env.NEXT_PUBLIC_SEJDA_API_KEY || null
const baseUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || window.location.origin
const assetBaseUrl =
  process.env.NEXT_PUBLIC_IMAGEKIT_URL_ENDPOINT || 'https://assets.jerseyfinance.com'

export default function DownloadBtn({
  filename,
  btnClass = 'inline-flex h-40 w-40 items-center justify-center rounded-sm text-fg-title hover:bg-theme-accent transition-colors duration-200 ease-in-out',
}: {
  filename?: string
  btnClass?: string
}) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [downloadName, setFilename] = useState(filename ?? null)
  const btnRef = useRef(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setFilename(document.title)
    }
  }, [])

  function makeUrlsAbsolute(doc: string) {
    try {
      // convert HTML string into a document
      const parser = new DOMParser()
      const docObj = parser.parseFromString(doc, 'text/html')

      docObj.querySelectorAll('link[href]').forEach((link) => {
        const href = link.getAttribute('href')
        if (href && !href.startsWith('http') && !href.startsWith('//')) {
          link.setAttribute('href', new URL(href, baseUrl).href)
        }
      })

      // Remove all script tags to prevent JS errors in PDF generation
      docObj.querySelectorAll('script').forEach((script) => {
        script.remove()
      })

      docObj.querySelectorAll('img[src]').forEach((img) => {
        const src = img.getAttribute('src')

        if (src && !src.startsWith('http') && !src.startsWith('//')) {
          img.setAttribute('src', new URL(src, assetBaseUrl).href)
        }
      })

      docObj.querySelectorAll('a[href]').forEach((anchor) => {
        const href = anchor.getAttribute('href')
        if (href && !href.startsWith('http') && !href.startsWith('//') && !href.startsWith('#')) {
          anchor.setAttribute('href', new URL(href, baseUrl).href)
        }
      })

      // return doc as string
      return docObj.documentElement.outerHTML
    } catch (err) {
      console.error('Error making URLs absolute: ', err)
    }
  }

  function handleDownload(filename: string | null = null) {
    if (!apiKey) {
      console.error('Sejda API key is missing')
      return
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      console.error('This function should be called in the browser')
      return
    }

    const timestamp = Date.now()
    const url = `${window.location.href}?convert_optout=1&time=${timestamp}`

    setIsProcessing(true)

    fetch(url, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
      },
    })
      .then((res) => res.text())
      .then((text) => {
        const pageData = makeUrlsAbsolute(text)

        // console.log('pageData parsed: ', pageData)

        fetch('https://api.sejda.com/v2/html-pdf', {
          cache: 'no-cache',
          method: 'POST',
          headers: {
            'Authorization': 'Token: ' + apiKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filename: `${filename}-${timestamp}.pdf`,
            pageOrientation: 'portrait',
            pageSize: 'a4',
            pageMargin: 0,
            usePrintMedia: true,
            htmlCode: pageData,
          }),
        })
          .then((response) => {
            if (!response.ok) throw new Error('Got code: ' + response.status)
            const pageBlob = response.blob()

            return pageBlob
          })

          .then((blob) => {
            const url = window.URL.createObjectURL(blob)

            const a = document.createElement('a')
            a.href = url
            a.download = `${filename}-${timestamp}.pdf`
            document.body.appendChild(a)

            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
            setIsProcessing(false)
          })
          .catch((err) => {
            setIsProcessing(false)
            console.error(err)
          })
      })
  }

  if (!apiKey) return null

  const finalBtnClass = `${btnClass} ${isProcessing ? 'animate-pulse bg-primary-200' : ''}`

  return (
    <TooltipTrigger delay={0}>
      <Button className={finalBtnClass} onPress={() => handleDownload(downloadName)} ref={btnRef}>
        <svg className="pointer-events-none h-24 w-24" aria-hidden="true">
          <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-download`}></use>
        </svg>
      </Button>
      <Tooltip>
        <OverlayArrow>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>
        {isProcessing ? 'Generating...' : 'Download'}
      </Tooltip>
    </TooltipTrigger>
  )
}
